import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from "xlsx";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  TrendingUp,
  AlertCircle,
  Clock,
  Plus,
  Target,
  Zap,
  X,
  Star,
  Search,
  ArrowUpDown,
  ChevronUp,
  ChevronDown,
  Eye,
  Download,
  Loader2,
  FileText,
  Trash2,
  Users,
  UserMinus,
} from 'lucide-react';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationPageSize,
} from "@/components/ui/pagination";
import NoData from "@/components/ui/NoData";
import PIPFormDialog from "./PIPFormDialog";
import OneTimeReviewDialog from "./OneTimeReviewDialog";
import AppraisalCycleDialog from "./AppraisalCycleDialog";
import AssignExitInterviewDialog from "./AssignExitInterviewDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNavigate } from 'react-router-dom';
import { toast } from "sonner";
import { getEmployees } from '@/services/adminService';
import { getTemplates } from '@/services/templateService';
import { getAllReviews, bulkAssignAppraisals, sendReminder, cancelReview, deleteReview } from '@/services/performanceService';
import { usePermission } from '@/contexts/RoleAccessContext';
import { createCanHelpers } from '@/utils/rbac';

import { Textarea } from '@/components/ui/textarea';
import { useSubscription } from "@/contexts/SubscriptionContext";

// --- KPI Card ---
const KPICard = ({ title, value, icon: Icon, colorClass }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg ${colorClass}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <h3 className="text-2xl font-bold">{value}</h3>
      <p className="text-xs text-muted-foreground font-medium">{title}</p>
    </CardContent>
  </Card>
);

const StarDisplay = ({ rating }) => {
  const full = Math.floor(rating);
  const hasHalf = rating % 1 >= 0.3;
  return (
    <div className="flex items-center gap-1">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map(i => (
          <Star key={i} size={13} className={i <= full ? "text-primary fill-primary" : (i === full + 1 && hasHalf) ? "text-primary fill-primary/80" : "text-muted-foreground"} />
        ))}
      </div>
      <span className="text-muted-foreground text-xs ml-1">{rating.toFixed(1)}</span>
    </div>
  );
};

const DueDateCell = ({ dueDate, reviewStatus, reviewType }) => {
  if (!dueDate) return "N/A";

  const due = new Date(dueDate);
  const dueMid = new Date(due);
  dueMid.setHours(0, 0, 0, 0);
  const todayMid = new Date();
  todayMid.setHours(0, 0, 0, 0);

  const isOverdue =
    dueMid < todayMid && !["Completed", "Cancelled"].includes(reviewStatus);
  const daysOverdue = Math.floor((todayMid - dueMid) / (1000 * 60 * 60 * 24));
  const formatted = due.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  if (!isOverdue) return formatted;

  return (
    <div className="flex gap-1.5 flex-wrap">
      <span className="text-red-600 font-semibold">{formatted}</span>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help">
              <AlertCircle className="h-3.5 w-3.5 text-red-600 shrink-0" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[220px]">
            <p className="text-xs">
              This {reviewType?.toLowerCase() || "review"} is overdue by{" "}
              {daysOverdue} {daysOverdue === 1 ? "day" : "days"} (past due
              date).
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const config = {
    "Pending": "bg-muted text-muted-foreground border-muted-foreground/20 dark:bg-muted/30 dark:text-muted-foreground dark:border-muted-foreground/30",
    "Draft": "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-900/30 dark:text-slate-300 dark:border-slate-800",
    "Self Review": "border-primary/20 text-primary bg-primary/10 dark:border-primary/80 dark:bg-primary/10 dark:text-primary",
    "Approval Review": "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800",
    "Admin Review": "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
    "Employee Comment": "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800",
    "Completed": "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
    "Cancelled": "bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
  };
  const displayStatus = status === 'Draft' ? 'Pending' : status;
  return <Badge variant="outline" className={`text-[10px] ${config[status] || "bg-gray-100 text-gray-700 border-gray-200"}`}>{displayStatus || "N/A"}</Badge>;
};

const typeConfig = {
  "Appraisal": { badge: "border-primary/20 text-primary bg-primary/10 dark:border-primary/80 dark:bg-primary/10 dark:text-primary" },
  "PIP": { badge: "border-orange-200 text-orange-600 bg-orange-50 dark:border-orange-800 dark:text-orange-400 dark:bg-orange-950/30" },
  "Exit Interview": { badge: "border-red-200 text-red-600 bg-red-50 dark:border-red-800 dark:text-red-400 dark:bg-red-950/30" },
};

// Map backend employee data to Dashboard format
const mapEmployeeForDashboard = (emp) => ({
  id: emp.employee_id,
  _id: emp._id,
  name: emp.name || "Unknown",
  department: emp.department || "N/A",
  designation: emp.designation || "N/A",
  manager: emp.manager || emp.reporting_manager || "N/A",
  rating: emp.rating || 0,
  lastReview: emp.last_review_date || "2025-01-01",
  status: "Pending",
  goalCompletion: 0,
  pip: false,
});

const mapReviewForTable = (review) => {
  const employee = review.employee_id;
  return {
    id: employee?.employee_id || "N/A",
    _id: employee?._id || null,
    reviewId: review._id,
    cycleId: review.cycle_id || null,
    name: review?.employee_name || "Unknown",
    department: employee?.department || "N/A",
    designation: employee?.designation || "N/A",
    manager: "N/A",
    rating: 0,
    lastReview: "2025-01-01",
    status: "Pending",
    goalCompletion: 0,
    pip: false,
    // Review-specific fields
    reviewType: review.review_type || "N/A",
    cycleName: review.cycle_name || "No Active Review",
    reviewStatus: review.status || "No Review",
    managerRating: review.manager_review?.overall_rating || 0,
    lastReviewDate: review.updatedAt || null,
    createdAt: review.createdAt || null,
    dueDate: review.due_date || null,
    templateName: review.template_id?.name || "N/A",
    // Navigation IDs
    appraisalId: review.review_type === 'Appraisal' ? review._id : null,
    appraisalStatus: review.review_type === 'Appraisal' ? review.status : null,
    pipId: review.review_type === 'PIP' ? review._id : null,
    pipStatus: review.review_type === 'PIP' ? review.status : null,
  };
};

// ===== MAIN COMPONENT =====
const PerformanceDashboard = () => {
  const navigate = useNavigate();
  const { can } = usePermission();
  const { canView, canCreate, canDelete,canReview } = createCanHelpers(can);

  const canViewReview = canView("performance_management");
  const canCreateAppraisal = canCreate("performance_management");

  // Departments are a paid feature — the column is hidden, never blanked.
  const { hasFeature } = useSubscription();
  const canUseDepartments = hasFeature("department_mapping");

  // Which performance action dialog is open, driven by the "Actions" dropdown.
  // One of: "oneTime" | "pip" | "cycle" | "exit" | null.
  const [perfAction, setPerfAction] = useState(null);
  const canViewAppraisal = canView("performance_management");

  const canViewPIP = canView("performance_management");
  const canCreatePIP = canCreate("performance_management");

  const canViewExitInterview = canView("performance_management");

  const [reviews, setReviews] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kpiLoading, setKpiLoading] = useState(true);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setKpiLoading(true);

      const allReviewsData = await getAllReviews();
      console.log("All Reviews Data:", allReviewsData);
      const allReviewsList = allReviewsData.data || allReviewsData;

      // Remove duplicates based on review _id
      const uniqueReviews = [];
      const seenIds = new Set();
      allReviewsList.forEach(review => {
        if (!seenIds.has(review._id)) {
          seenIds.add(review._id);
          uniqueReviews.push(review);
        }
      });

      const mappedReviews = uniqueReviews.map(mapReviewForTable);
      setReviews(mappedReviews);

      const employeeData = await getEmployees(undefined, "all", { statuses: ['active', "exit_scheduled"], scope: 'performance' });
      const mappedEmployeeData = employeeData.map(mapEmployeeForDashboard);
      setEmployeeList(mappedEmployeeData);
    }
    catch (error) {
      console.error("Error fetching employee data on appraisal dashboard:", error)
      toast.error("Error fetching employee data on appraisal dashboard")
      setReviews([]);
      setEmployeeList([]);
    }
    finally {
      setLoading(false);
      setKpiLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Table state
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const handlePageSizeChange = (value) => {
    setPageSize(Number(value));
    setCurrentPage(1); // reset to first page when page size changes
  };

  const [cancelTargetReview, setCancelTargetReview] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const MIN_CANCEL_REASON_LENGTH = 10;
  const normalizedCancelReason = cancelReason.trim();

  const [deleteTargetReview, setDeleteTargetReview] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Bulk action state
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [bulkTemplateId, setBulkTemplateId] = useState("");
  const [bulkCycleName, setBulkCycleName] = useState("");
  const [bulkCreating, setBulkCreating] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const canCancelReviewRecord = (review) => {
    if (!canCreate("performance_management")) return false;

    if (["Appraisal", "PIP", "Exit Interview"].includes(review.reviewType)){
      return !["Completed", "Cancelled"].includes(review.reviewStatus);
    }
    return false;
  };

  const canDeleteReviewRecord = (review) => {
    if (!canDelete("performance_management")) return false;

    if (["Appraisal", "PIP", "Exit Interview"].includes(review.reviewType)){
      return review.reviewStatus === "Cancelled";
    }
    return false;
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templateData = await getTemplates();
        if (Array.isArray(templateData)) {
          setTemplates(templateData);
        } else if (templateData && Array.isArray(templateData.data)) {
          setTemplates(templateData.data);
        } else {
          console.warn('Unexpected template data format:', templateData);
          setTemplates([]);
        }
      } catch (error) {
        console.error("Error fetching templates on appraisal dashboard :", error);
        setTemplates([]);
      }
    }

    fetchTemplates();

  }, []);


  const handleBulkCreate = async () => {
    if (!canCreate("performance_management", true)) return;

    if (!bulkTemplateId) { toast.error("Select a template"); return; }
    setBulkCreating(true);
    try {
      const response = await bulkAssignAppraisals(
        selectedRows,
        bulkTemplateId,
        bulkCycleName || `Appraisal Cycle - ${new Date().toLocaleDateString()}`,
        {
          startDate: new Date().toISOString(),
          deadline: undefined
        }
      );

      if (response?.data?.created > 0) {
        if (response?.data?.errors?.length > 0) {
          toast.info(`Created ${response.data.created} appraisals. ${response.data.errors.length} skipped.`);
        } else {
          toast.success(`Successfully created ${response.data.created} appraisals!`);
        }
        setIsBulkOpen(false);
        setSelectedRows([]);
        setBulkTemplateId("");
        setBulkCycleName("");
        fetchDashboardData();
      } else {
        toast.error("No appraisals created.");
      }
    } catch (error) {
      console.error("Error creating bulk appraisals:", error);
      toast.error(error.response?.data?.error || error.response?.data?.message || "Failed to create appraisals");
    } finally { setBulkCreating(false); }
  };

  const exportHeaders = [
    "Employee ID",
    "Name",
    "Department",
    "Template Name",
    "Review Type",
    "Rating",
    "Status",
    "Cycle",
    "Last Review",
    "Due Date"
  ];

  const getExportRows = () => filteredReviews.map(e => ({
    "Employee ID": e.id,
    "Name": e.name,
    "Department": e.department,
    "Template Name": e.templateName,
    "Review Type": e.reviewType,
    "Rating": e.managerRating,
    "Status": e.reviewStatus,
    "Cycle": e.cycleName,
    "Last Review": e.lastReviewDate ? new Date(e.lastReviewDate).toLocaleDateString() : "Never",
    "Due Date": e.dueDate ? new Date(e.dueDate).toLocaleDateString() : "N/A"
  }));

  const handleExportExcel = () => {
    const rows = getExportRows();
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: exportHeaders });
    worksheet["!cols"] = exportHeaders.map(header => ({
      wch: Math.max(header.length + 2, 14)
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Employee Reviews");
    XLSX.writeFile(workbook, `employee-reviews-${new Date().toISOString().split("T")[0]}.xlsx`);
    toast.success("Report exported as Excel");
  };
  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  const filteredReviews = useMemo(() => {
    // Remove duplicates based on reviewId or _id
    const uniqueReviewsMap = new Map();
    reviews.forEach(review => {
      const key = review.reviewId || review._id || review.id;
      if (!uniqueReviewsMap.has(key)) {
        uniqueReviewsMap.set(key, review);
      }
    });
    const uniqueReviews = Array.from(uniqueReviewsMap.values());

    return uniqueReviews
      .filter(e =>
        e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.cycleName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.reviewType.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.reviewStatus.toLowerCase().includes(searchQuery.toLowerCase())
      )
      .sort((a, b) => {
        const dir = sortDir === "asc" ? 1 : -1;
        if (typeof a[sortField] === "number") return (a[sortField] - b[sortField]) * dir;
        return String(a[sortField]).localeCompare(String(b[sortField])) * dir;
      });
  }, [reviews, searchQuery, sortField, sortDir]);

  const totalReviewsPages = Math.ceil(filteredReviews.length / pageSize);
  const paginatedReviews = filteredReviews.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sortField, sortDir]);

  const SortHeader = ({ field, children }) => (
    <button className="flex items-center gap-1 hover:text-primary transition-colors" onClick={() => handleSort(field)} data-testid={`sort-${field}`}>
      {children}
      {sortField === field ? (sortDir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-30" />}
    </button>
  );


  const handleSendReminder = async () => {
    if (!canCreate("performance_management", true)) return;

    if (selectedRows.length === 0) {
      toast.error("Please select at least one employee");
      return;
    }

    setReminderSending(true);
    try {
      const reviewIds = selectedRows;
      const response = await sendReminder(reviewIds);

      const sent = response?.data?.sent || 0;
      const skippedCompleted = response?.data?.skippedCompleted || 0;
      const skippedCancelled = response?.data?.skippedCancelled || 0;

      if (sent > 0) {
        let message = `Reminder sent to ${sent} employee(s)!`;

        if (skippedCompleted > 0) {
          message += ` ${skippedCompleted} completed skipped.`;
        }

        if (skippedCancelled > 0) {
          message += ` ${skippedCancelled} cancelled skipped.`;
        }

        toast.success(message);
      } else {
        if (skippedCompleted > 0 && skippedCancelled > 0) {
          toast.info(
            "Selected reviews are either completed or cancelled. No reminders were sent."
          );
        } else if (skippedCompleted > 0) {
          toast.info(
            "Selected reviews are already completed. No reminders were sent."
          );
        } else if (skippedCancelled > 0) {
          toast.info(
            "Selected reviews are cancelled. No reminders were sent."
          );
        } else {
          toast.info("No reminders were sent.");
        }
      }
    } catch (error) {
      console.error("Error sending reminders : ", error);
      toast.error(
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to send reminders"
      );
    } finally {
      setReminderSending(false);
    }
  };

  const handleCancelReview = async () => {
    if (!cancelTargetReview) return;
    if (!normalizedCancelReason) {
      toast.error(`Please provide at least ${MIN_CANCEL_REASON_LENGTH} characters for cancellation reason`);
      return;
    }


    setCancelSubmitting(true);
    try {
      await cancelReview(cancelTargetReview.reviewId, normalizedCancelReason);
      toast.success(`Review cancelled successfully`)
      setCancelTargetReview(null);
      setCancelReason("");
      setSelectedRows((prev) => prev.filter((id) => id !== cancelTargetReview.reviewId));
      fetchDashboardData();
    } catch (error) {
      console.error("Error canceling review : ", error);
      toast.error(error.response?.data?.message || "Failed to cancel review");
    } finally {
      setCancelSubmitting(false);
    }

  };

   const handleDeleteReview = async () => {
    if (!deleteTargetReview) return;

    if (!canDelete("performance_management", true)) return;

    if (deleteTargetReview.reviewStatus !== "Cancelled") {
      toast.error("Only cancelled reviews can be deleted");
      return;
    }

    setDeleteSubmitting(true);
    try{
      await deleteReview(deleteTargetReview.reviewId);
      toast.success(`Review deleted successfully`)
      setDeleteTargetReview(null);
      setSelectedRows((prev) => prev.filter((id) => id !== deleteTargetReview.reviewId));
      fetchDashboardData();
    } catch (error) {
      console.error("Error deleting review : ", error);
      toast.error(error.response?.data?.error || "Failed to delete review");
    } finally {
      setDeleteSubmitting(false);
    }
  }

  const allReviews = reviews;
  const pendingReviews = allReviews.filter(r => !['Completed', 'Cancelled'].includes(r.reviewStatus)).length;
  const completedReviewsWithRating = allReviews.filter(r => r.reviewStatus === 'Completed' && r.managerRating > 0);
  const avgPerformance = completedReviewsWithRating.length > 0
    ? (completedReviewsWithRating.reduce((sum, r) => sum + r.managerRating, 0) / completedReviewsWithRating.length).toFixed(1)
    : "0";
  const totalReviews = allReviews.length;
  const completedCount = allReviews.filter(r => r.reviewStatus === 'Completed').length;
  const reviewsCompletedPercentage = totalReviews > 0 ? Math.round((completedCount / totalReviews) * 100) : 0;
  const employeesOnPIP = allReviews.filter(r => r.reviewType === 'PIP' && !['Completed', 'Cancelled'].includes(r.reviewStatus)).length;

  return (
    <>
      {/* Page Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1
            className="text-3xl font-bold tracking-tight text-foreground"
            data-testid="performance-heading"
          >
            Overview
          </h1>
          <p className="text-muted-foreground mt-1">
            Performance analytics and employee ratings at a glance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                disabled={!canCreateAppraisal}
                data-testid="performance-actions-btn"
              >
                Assign
                <ChevronDown className="ml-1.5 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onSelect={() => setPerfAction("cycle")}>
                <Plus className="mr-2 h-4 w-4" /> New Appraisal Cycle
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setPerfAction("oneTime")}>
                <Zap className="mr-2 h-4 w-4" /> One-Time Review
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setPerfAction("pip")}>
                <AlertCircle className="mr-2 h-4 w-4" /> PIP
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setPerfAction("exit")}>
                <UserMinus className="mr-2 h-4 w-4" /> Exit Interview
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Dialogs are controlled by the dropdown above (no own triggers). */}
          <OneTimeReviewDialog
            employeeList={employeeList}
            templates={templates}
            onCreated={fetchDashboardData}
            hideTrigger
            open={perfAction === "oneTime"}
            onOpenChange={(o) => setPerfAction(o ? "oneTime" : null)}
          />
          <PIPFormDialog
            employeeList={employeeList}
            templates={templates}
            onCreated={fetchDashboardData}
            hideTrigger
            open={perfAction === "pip"}
            onOpenChange={(o) => setPerfAction(o ? "pip" : null)}
          />
          <AppraisalCycleDialog
            employeeList={employeeList}
            templates={templates}
            onCreated={fetchDashboardData}
            hideTrigger
            open={perfAction === "cycle"}
            onOpenChange={(o) => setPerfAction(o ? "cycle" : null)}
          />
          <AssignExitInterviewDialog
            templates={templates}
            onCreated={fetchDashboardData}
            hideTrigger
            open={perfAction === "exit"}
            onOpenChange={(o) => setPerfAction(o ? "exit" : null)}
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 mb-6">
        <KPICard
          title="Pending Reviews"
          value={kpiLoading ? "..." : pendingReviews.toString()}
          trendUp={false}
          icon={Clock}
          colorClass="text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary"
        />

        <KPICard
          title="Avg. Performance"
          value={kpiLoading ? "..." : avgPerformance}
          trendUp={true}
          icon={TrendingUp}
          colorClass="text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary"
        />

        <KPICard
          title="Reviews Completed"
          value={kpiLoading ? "..." : reviewsCompletedPercentage + "%"}
          trendUp={true}
          icon={Target}
          colorClass="text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary"
        />

        <KPICard
          title="Employees on PIP"
          value={kpiLoading ? "..." : employeesOnPIP.toString()}
          trendUp={true}
          icon={AlertCircle}
          colorClass="text-primary bg-primary/10 dark:bg-primary/30 dark:text-primary"
        />
      </div>

      {/* Employee Table */}
      <Card data-testid="employee-table-card">
        <CardHeader className="pb-3">
          <div className="flex justify-between">
            <div>
              <CardTitle className="text-base">All Reviews</CardTitle>
              <CardDescription>
                Employee performance reviews and appraisal status.
              </CardDescription>
            </div>
            <div>
              {/* Filter Row */}
              <div className="flex items-center gap-2 justify-end">
                {selectedRows.length > 0 && (
                  <>
                    <Badge variant="secondary">
                      {selectedRows.length} selected
                    </Badge>
                    <Button
                      size="default"
                      variant="outline"
                      className={`text-xs ${
                        !canCreate ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                      onClick={() => {
                        if (!canCreate("performance_management", true)) return;
                        handleSendReminder();
                      }}
                      disabled={
                        reminderSending || !canCreate("performance_management")
                      }
                      data-testid="bulk-remind-btn"
                    >
                      {reminderSending ? (
                        <>
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />{" "}
                          Sending...
                        </>
                      ) : (
                        "Send Reminder"
                      )}
                    </Button>
                    {/* <Button 
                      size="default" 
                      className={`text-xs bg-primary hover:bg-primary/90 gap-1 ${
                        !canCreateAppraisal ? "opacity-70 cursor-not-allowed" : ""
                      }`}
                      onClick={() => {
                        if (!canCreate("performance_management", true)) return;
                        setIsBulkOpen(true);
                      }}
                      disabled={!canCreateAppraisal}
                      data-testid="bulk-appraisal-btn"
                    >
                      <Plus className="h-3 w-3" /> Bulk Start Appraisal
                    </Button> */}
                  </>
                )}
                {/* Search Bar — only when there are records to search */}
                {reviews.length > 0 && (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search employees..."
                    className="pl-8 h-9 w-[250px] text-sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    data-testid="employee-search"
                  />
                  {searchQuery && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6"
                      onClick={() => setSearchQuery("")}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                )}
                <Button
                  size="default"
                  variant="outline"
                  className="text-xs gap-1"
                  data-testid="export-excel-btn"
                  disabled={
                    reminderSending || !canCreate("performance_management")
                  }
                  onClick={handleExportExcel}
                >
                  <Download className="h-3 w-3" />
                  Export
                </Button>
                {loading && <Badge variant="outline">Loading...</Badge>}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {!loading && paginatedReviews.length === 0 ? (
            <div className="flex flex-col items-center">
              <NoData
                icon={Users}
                title="No employees found"
                message={
                  searchQuery
                    ? "Try adjusting your search query"
                    : "Start by creating a new appraisal cycle"
                }
              />
              {!searchQuery && (
                <Button
                  onClick={() => setIsCycleWizardOpen(true)}
                  className="mb-8 -mt-4"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Appraisal Cycle
                </Button>
              )}
            </div>
          ) : (
          <div className="overflow-x-auto thin-scrollbar">
            <table className="w-full text-sm min-w-[1600px]" data-testid="employee-table">
              <thead>
                <tr className="border-y border-gray-200 dark:border-gray-700">
                  <th className="py-3 px-4 text-left w-12 text-gray-700 dark:text-gray-300">
                    <Checkbox
                      checked={
                        selectedRows.length === paginatedReviews.length &&
                        paginatedReviews.length > 0
                      }
                      onCheckedChange={(c) =>
                        setSelectedRows(
                          c ? paginatedReviews.map((e) => e.reviewId) : [],
                        )
                      }
                      data-testid="select-all"
                    />
                  </th>
                  <th className="py-3 px-4 text-left min-w-[220px] text-gray-700 dark:text-gray-300">
                    <SortHeader field="name">Employee</SortHeader>
                  </th>
                  {canUseDepartments && (
                    <th className="py-3 px-4 text-left min-w-[140px] text-gray-700 dark:text-gray-300">
                      <SortHeader field="department">Department</SortHeader>
                    </th>
                  )}
                  <th className="py-3 px-4 text-left min-w-[120px] text-gray-700 dark:text-gray-300">
                    <SortHeader field="reviewType">Review Type</SortHeader>
                  </th>
                  <th className="py-3 px-4 text-left min-w-[150px] text-gray-700 dark:text-gray-300">
                    <SortHeader field="managerRating">Rating</SortHeader>
                  </th>
                  <th className="py-3 px-4 text-left min-w-[140px] text-gray-700 dark:text-gray-300">
                    <SortHeader field="reviewStatus">Status</SortHeader>
                  </th>
                  <th className="py-3 px-4 text-left min-w-[200px] text-gray-700 dark:text-gray-300">
                    <SortHeader field="designation">Template Name</SortHeader>
                  </th>
                  <th className="py-3 px-4 text-left min-w-[180px] text-gray-700 dark:text-gray-300">
                    <SortHeader field="cycleName">Cycle</SortHeader>
                  </th>
                  <th className="py-3 px-4 text-left min-w-[120px] whitespace-nowrap text-gray-700 dark:text-gray-300">
                    <SortHeader field="lastReviewDate">Last Review</SortHeader>
                  </th>
                  <th className="py-3 px-4 text-left min-w-[130px] whitespace-nowrap text-gray-700 dark:text-gray-300">
                    <SortHeader field="dueDate">Due Date</SortHeader>
                  </th>
                  <th className="py-3 px-4 text-right min-w-[130px] sticky right-0 bg-card z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] text-gray-700 dark:text-gray-300">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={canUseDepartments ? 11 : 10} className="py-16 text-center">
                      <div className="flex flex-col items-center justify-center gap-3">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                          Loading employees...
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedReviews.map((emp) => (
                    <tr
                      key={emp.reviewId || emp._id || emp.id}
                      className="border-b border-muted hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => {
                        // Same view action as the row's Eye button
                        if (!canReview("performance_management", true)) return;

                        if (
                          emp.reviewType === "Appraisal" &&
                          emp.appraisalId
                        ) {
                          navigate(`/performance/review/${emp.appraisalId}`);
                        } else if (emp.reviewType === "PIP" && emp.pipId) {
                          navigate(`/performance/pip/${emp.pipId}`);
                        } else if (emp.reviewType === "Exit Interview") {
                          navigate(
                            `/performance/exit-interview/${emp.reviewId}`,
                          );
                        } else {
                          toast.info("No review available to view");
                        }
                      }}
                    >
                      {/* Checkbox */}
                      <td
                        className="py-3 px-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedRows.includes(emp.reviewId)}
                          onCheckedChange={(c) =>
                            setSelectedRows(
                              c
                                ? [...selectedRows, emp.reviewId]
                                : selectedRows.filter(
                                    (id) => id !== emp.reviewId,
                                  ),
                            )
                          }
                        />
                      </td>

                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2.5">
                          <Avatar className="h-7 w-7">
                            <AvatarFallback className="text-[10px] bg-primary/10 text-primary font-bold">
                              {emp.name
                                .split(" ")
                                .map((n) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-foreground text-sm">
                              {emp.name}
                            </p>
                            <p className="text-[10px] text-muted-foreground">
                              {emp.id}
                            </p>
                          </div>
                        </div>
                      </td>

                      {canUseDepartments && (
                        <td className="py-3 px-4 text-muted-foreground text-xs">
                          {emp.department}
                        </td>
                      )}

                      <td className="py-3 px-4">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${typeConfig[emp.reviewType]?.badge || "bg-gray-100 text-gray-700 border-gray-200"}`}
                        >
                          {emp.reviewType === "Appraisal"
                            ? "Appraisal"
                            : emp.reviewType === "PIP"
                              ? "PIP"
                              : emp.reviewType === "Exit Interview"
                                ? "Exit"
                                : "N/A"}
                        </Badge>
                      </td>

                      <td className="py-3 px-4">
                        {emp.managerRating > 0 ? (
                          <StarDisplay rating={emp.managerRating} />
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            Not rated
                          </span>
                        )}
                      </td>

                      <td className="py-3 px-4">
                        <StatusBadge status={emp.reviewStatus} />
                      </td>

                      <td className="py-3 px-4 text-muted-foreground text-xs">
                        {emp.templateName}
                      </td>

                      <td className="py-3 px-4 text-muted-foreground text-xs">
                        {emp.cycleName}
                      </td>

                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {emp.lastReviewDate
                          ? new Date(emp.lastReviewDate).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )
                          : "Never"}
                      </td>

                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        <DueDateCell
                          dueDate={emp.dueDate}
                          reviewStatus={emp.reviewStatus}
                          reviewType={emp.reviewType}
                        />
                      </td>

                      <td
                        className="py-3 px-4 sticky right-0 bg-card z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 w-7 p-0 hover:bg-primary/10 hover:text-primary disabled:opacity-100 ${
                              !canViewReview ? "cursor-not-allowed" : ""
                            }`}
                            onClick={() => {
                              // All review types use the same "reviews" feature
                              if (!canReview("performance_management", true))
                                return;

                              if (
                                emp.reviewType === "Appraisal" &&
                                emp.appraisalId
                              ) {
                                navigate(
                                  `/performance/review/${emp.appraisalId}`,
                                );
                              } else if (
                                emp.reviewType === "PIP" &&
                                emp.pipId
                              ) {
                                navigate(`/performance/pip/${emp.pipId}`);
                              } else if (emp.reviewType === "Exit Interview") {
                                navigate(
                                  `/performance/exit-interview/${emp.reviewId}`,
                                );
                              } else {
                                toast.info("No review available to view");
                              }
                            }}
                            title="View Review"
                            disabled={!canViewReview}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {canCancelReviewRecord(emp) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => {
                                setCancelTargetReview(emp);
                                setCancelReason("");
                              }}
                              title={`Cancel ${emp.reviewType}`}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                          {canDeleteReviewRecord(emp) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => {
                                setDeleteTargetReview(emp);
                              }}
                              title="Delete Review"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          )}
        </CardContent>
        {filteredReviews.length > 0 && (
        <CardFooter className="py-3">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground whitespace-nowrap">
                Showing{" "}
                {Math.min(
                  (currentPage - 1) * pageSize + 1,
                  filteredReviews.length,
                )}{" "}
                to {Math.min(currentPage * pageSize, filteredReviews.length)} of{" "}
                {filteredReviews.length} entries
              </div>
              <PaginationPageSize
                value={pageSize}
                onValueChange={handlePageSizeChange}
              />
            </div>
            <Pagination className="!justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() =>
                      setCurrentPage(Math.max(1, currentPage - 1))
                    }
                    className={
                      currentPage <= 1
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                {[
                  1,
                  ...Array.from(
                    { length: Math.min(5, totalReviewsPages) },
                    (_, i) => {
                      const start = Math.max(
                        2,
                        Math.min(currentPage - 2, totalReviewsPages - 4),
                      );
                      return start + i;
                    },
                  ).filter((p) => p > 1 && p < totalReviewsPages),
                  totalReviewsPages,
                ]
                  .filter((p, i, arr) => p && arr.indexOf(p) === i)
                  .reduce((acc, page, index, arr) => {
                    if (index === 0) return [page];
                    const prev = arr[index - 1];
                    if (page - prev === 2) return [...acc, prev + 1, page];
                    if (page - prev > 1) return [...acc, "...", page];
                    return [...acc, page];
                  }, [])
                  .map((page, index) => (
                    <PaginationItem key={index}>
                      {page === "..." ? (
                        <span className="px-2 text-muted-foreground">
                          ...
                        </span>
                      ) : (
                        <PaginationLink
                          isActive={page === currentPage}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() =>
                      setCurrentPage(
                        Math.min(totalReviewsPages, currentPage + 1),
                      )
                    }
                    className={
                      currentPage >= totalReviewsPages
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </CardFooter>
        )}
      </Card>

      {/* Bulk Appraisal Dialog */}
      <Dialog open={isBulkOpen} onOpenChange={setIsBulkOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Bulk Start Appraisals</DialogTitle>
            <DialogDescription>
              Create appraisals for {selectedRows.length} selected employees.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Cycle Name</Label>
              <Input
                value={bulkCycleName}
                onChange={(e) => setBulkCycleName(e.target.value)}
                placeholder="e.g., Q1 2025 Performance Review"
                data-testid="bulk-cycle-name"
              />
            </div>
            <div className="grid gap-2">
              <Label>Appraisal Template</Label>
              <Select value={bulkTemplateId} onValueChange={setBulkTemplateId}>
                <SelectTrigger data-testid="bulk-template-select">
                  <SelectValue placeholder="Select template..." />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(templates) &&
                    templates
                      .filter((t) => t.type === "Appraisal")
                      .map((t) => (
                        <SelectItem key={t._id || t.id} value={t._id || t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 rounded-lg bg-muted/50 border text-sm">
              <p className="font-medium text-foreground mb-1">
                Selected Employees:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {selectedRows.map((eid) => {
                  const emp = reviews.find((e) => e.id === eid);
                  return (
                    <Badge key={eid} variant="outline" className="text-xs">
                      {emp?.name || eid}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsBulkOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90 gap-2"
              onClick={handleBulkCreate}
              disabled={bulkCreating || !bulkTemplateId}
              data-testid="bulk-create-btn"
            >
              {bulkCreating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}{" "}
              Create {selectedRows.length} Appraisals
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(cancelTargetReview)}
        onOpenChange={(open) => {
          if (!open && !cancelSubmitting) {
            setCancelTargetReview(null);
            setCancelReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Cancel {cancelTargetReview?.reviewType || "Review"}
            </DialogTitle>
            <DialogDescription>
              This will mark the review as cancelled. The record will remain in
              history.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <p className="text-sm">
              <span className="font-medium">
                Please provide a valid cancellation reason
              </span>
            </p>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation"
              className="min-h-[90px] border-red-500 focus:border-red-600 focus-visible:ring-red-500"
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              className=" focus-visible:ring-red-500"
              onClick={() => setCancelTargetReview(null)}
              disabled={cancelSubmitting}
            >
              Keep Review
            </Button>
            <Button
              variant="destructive"
              className=" focus-visible:ring-red-500"
              onClick={handleCancelReview}
              disabled={cancelSubmitting || normalizedCancelReason.length === 0}
            >
              Cancel Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleteTargetReview)}
        onOpenChange={(open) => {
          if (!open && !deleteSubmitting) {
            setDeleteTargetReview(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              Delete {deleteTargetReview?.reviewType || "Review"}
            </DialogTitle>
            <DialogDescription>
              This will permanently delete the record. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              variant="outline"
              className=" focus-visible:ring-red-500"
              onClick={() => setDeleteTargetReview(null)}
              disabled={deleteSubmitting}
            >
              Keep Review
            </Button>
            <Button
              variant="destructive"
              className=" focus-visible:ring-red-500"
              onClick={handleDeleteReview}
              disabled={deleteSubmitting}
            >
              Delete Review
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PerformanceDashboard;
